import type { ClipCategory } from '@/sections/clip-categories/clip-category.types'

import { z } from 'zod'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import { paths } from '@/routes/paths'
import { Plus, X } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { firebaseStorage } from '@/lib/firebase'
import { useRef, useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import { useUploadGuard } from '@/hooks/use-upload-guard'
import RichTextEditor from '@/components/ui/rich-text-editor'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import type { AdminClip, VideoMetadata } from './clip.types'
import type { R2MultipartHandle, R2UploadProgress } from './r2-upload'

import { startR2MultipartUpload } from './r2-upload'
import ClipVideoUploader from './components/clip-video-uploader'
import ClipThumbnailPicker from './components/clip-thumbnail-picker'
import ClipCommentsManager from './components/clip-comments-manager'

// ----------------------------------------------------------------------

// Vite env vars are strings; compare explicitly so "false" stays OFF.
// (Mirrors src/utils/sentry.ts.) Default OFF: the live default is Firebase.
const CLIP_R2_UPLOAD_ENABLED =
  import.meta.env.VITE_CLIP_R2_UPLOAD_ENABLED === 'true'

const CLIP_MAX_UPLOAD_BYTES = (() => {
  const raw = Number(import.meta.env.VITE_CLIP_MAX_UPLOAD_BYTES)
  return Number.isFinite(raw) && raw > 0 ? raw : 2 * 1024 * 1024 * 1024
})()

const CLIP_R2_MULTIPART_PART_SIZE_BYTES = (() => {
  const raw = Number(import.meta.env.VITE_CLIP_R2_MULTIPART_PART_SIZE_BYTES)
  return Number.isFinite(raw) && raw > 0 ? raw : 10 * 1024 * 1024
})()

// Legacy Firebase cap stays 500 MB when the R2 flag is off, byte-for-byte.
const LEGACY_MAX_UPLOAD_BYTES = 500 * 1024 * 1024

const clipFormSchema = z.object({
  title: z.string().min(1, 'กรุณาป้อนชื่อคลิป'),
  description: z.string().optional(),
  tags: z.array(z.string()),
  category: z.string().min(1, 'กรุณาเลือกหมวดหมู่'),
  isPublished: z.boolean(),
})

type FormData = z.infer<typeof clipFormSchema>

interface ClipFormProps {
  clip?: AdminClip
  mode: 'create' | 'edit'
  variant?: 'page' | 'dialog'
  onCancel?: () => void
  onSuccess?: () => void
  /** Notifies the parent (dialog owner) when an R2 upload is in flight so it can
   *  lock the dialog against accidental close while uploading. */
  onUploadingChange?: (isUploading: boolean) => void
}

// Upload a File to Firebase Storage with progress callback; returns {storagePath, downloadUrl}
async function uploadToStorage(
  file: File,
  storagePath: string,
  onProgress?: (pct: number) => void,
): Promise<{ storagePath: string; downloadUrl: string }> {
  const storageRef = ref(firebaseStorage, storagePath)
  const task = uploadBytesResumable(storageRef, file)

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress) {
          onProgress((snap.bytesTransferred / snap.totalBytes) * 100)
        }
      },
      reject,
      resolve,
    )
  })

  const downloadUrl = await getDownloadURL(task.snapshot.ref)
  return { storagePath, downloadUrl }
}

// Convert dataURL → Blob → File
function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] ?? 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) u8arr[n] = bstr.charCodeAt(n)
  return new File([u8arr], filename, { type: mime })
}

// ----------------------------------------------------------------------

export default function ClipForm({
  clip,
  mode,
  variant = 'page',
  onCancel,
  onSuccess,
  onUploadingChange,
}: ClipFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)

  const [newTag, setNewTag] = useState('')
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState<string>(
    clip?.thumbnailUrl ?? '',
  )
  const [durationSec, setDurationSec] = useState<number>(clip?.durationSec ?? 0)
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null)
  const [videoProgress, setVideoProgress] = useState<number | null>(null)
  const [thumbProgress, setThumbProgress] = useState<number | null>(null)

  // R2 multipart upload state (only used when CLIP_R2_UPLOAD_ENABLED).
  const [r2Progress, setR2Progress] = useState<R2UploadProgress | null>(null)
  const [r2Failed, setR2Failed] = useState(false)
  // Explicit upload flag (not derived from r2Progress): it is true the instant we
  // start initiate (before the first progress emit) and is reset in a single
  // finally, so success/failure/abort all tear the guard down — no sticky guard.
  const [isUploading, setIsUploading] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const r2HandleRef = useRef<R2MultipartHandle | null>(null)

  const [categories, setCategories] = useState<ClipCategory[]>([])

  // Warn on refresh / tab-close while uploading (beforeunload). In-app nav is
  // covered by the modal Dialog overlay — see useUploadGuard for the rationale.
  useUploadGuard(isUploading)

  // Surface the uploading signal to the dialog owner so it can lock the dialog.
  useEffect(() => {
    onUploadingChange?.(isUploading)
  }, [isUploading, onUploadingChange])

  // Cancel any in-flight R2 multipart upload on unmount so we do not leave an
  // orphaned multipart upload in R2 (abort() is best-effort + lifecycle-backed).
  useEffect(() => {
    return () => {
      void r2HandleRef.current?.abort()
    }
  }, [])

  // On unmount, clear the owner's uploading lock. The declarative onUploadingChange
  // effect may not commit its `false` before the dialog closes/unmounts on success,
  // which would leave the dialog stuck-locked; this guarantees a terminal reset.
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange])

  useEffect(() => {
    axiosInstance
      .get(endpoints.clipCategories.list)
      .then((res) => {
        const items: ClipCategory[] = res.data ?? []
        setCategories(items)
      })
      .catch((err) => {
        console.error('fetch categories error', err)
      })
  }, [])

  const form = useForm<FormData>({
    resolver: zodResolver(clipFormSchema),
    defaultValues: {
      title: clip?.title ?? '',
      description: clip?.description ?? '',
      tags: clip?.tags ?? [],
      category: clip?.category ?? '',
      isPublished: clip?.isPublished ?? false,
    },
  })

  const handleVideoChange = (file: File | null) => {
    setVideoFile(file)
    setThumbnailDataUrl('')
  }

  const handleAddTag = () => {
    const trimmed = newTag.trim()
    if (trimmed && !form.getValues('tags').includes(trimmed)) {
      form.setValue('tags', [...form.getValues('tags'), trimmed])
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    form.setValue(
      'tags',
      form.getValues('tags').filter((t) => t !== tagToRemove),
    )
  }

  // R2 multipart create path (flag ON). Uploads the source via client-side
  // multipart PUTs to R2, uploads the optional thumbnail via a presigned PUT,
  // then submits the R2 input descriptor to POST /admin/clips (Decision 16).
  const handleR2Create = async (data: FormData, file: File) => {
    // Serialize uploads: a retry/submit while one is in flight would overwrite
    // r2HandleRef and orphan the first multipart upload.
    if (r2HandleRef.current) return

    if (file.size > CLIP_MAX_UPLOAD_BYTES) {
      const limitMb = Math.round(CLIP_MAX_UPLOAD_BYTES / (1024 * 1024))
      toast.error(`ไฟล์วิดีโอต้องมีขนาดไม่เกิน ${limitMb} MB`)
      return
    }

    const thumbnailFile = thumbnailDataUrl
      ? dataUrlToFile(thumbnailDataUrl, 'source-thumbnail.jpg')
      : null

    setR2Failed(false)
    setR2Progress(null)
    setIsUploading(true)

    const handle = startR2MultipartUpload({
      file,
      thumbnail: thumbnailFile,
      partSizeBytes: CLIP_R2_MULTIPART_PART_SIZE_BYTES,
      onProgress: setR2Progress,
    })
    r2HandleRef.current = handle

    try {
      const uploaded = await handle.done

      // R2-mode create payload: R2 input descriptor instead of Firebase paths.
      // The API derives clipId + mediaToken from the consumed upload session
      // (CreateAdminClipDto has no clipId/mediaToken field), so we send only the
      // session id + the keys it validates against that session.
      await axiosInstance.post(endpoints.clips.create, {
        title: data.title,
        description: data.description,
        uploadSessionId: uploaded.uploadSessionId,
        inputKey: uploaded.inputKey,
        ...(uploaded.thumbnailInputKey
          ? { thumbnailInputKey: uploaded.thumbnailInputKey }
          : {}),
        durationSec,
        ...(videoMetadata ?? {}),
        tags: data.tags,
        category: data.category,
        isPublished: data.isPublished,
      })

      toast.success('อัปโหลดสำเร็จ — กำลังประมวลผลวิดีโอ')
      if (onSuccess) {
        onSuccess()
      } else {
        router.replace(paths.clips)
      }
    } catch (err) {
      // User-initiated abort/unmount surfaces as a cancel/abort error; not a
      // failure the user should see.
      const name = (err as { name?: string; code?: string })?.name
      const code = (err as { name?: string; code?: string })?.code
      if (name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED') {
        return
      }
      console.error('R2 clip upload error', err)
      // Clear the dangling R2 multipart upload on hard failure (best-effort;
      // an R2 lifecycle rule also reaps aborted uploads after 1 day).
      void handle.abort()
      setR2Failed(true)
      toast.error('อัปโหลดล้มเหลว')
    } finally {
      r2HandleRef.current = null
      // Single teardown point: success, hard-fail, and abort all clear the
      // uploading flag here, so the dialog lock + beforeunload guard never stick.
      setIsUploading(false)
    }
  }

  // Abort the in-flight R2 upload (user cancel, after confirm). Cleans up the R2
  // multipart; the in-flight handleR2Create's finally clears isUploading via the
  // resulting AbortError, tearing down the dialog lock + beforeunload guard.
  const handleR2Abort = () => {
    setCancelConfirmOpen(false)
    void r2HandleRef.current?.abort()
    r2HandleRef.current = null
    setR2Progress(null)
    setR2Failed(false)
    toast.info('ยกเลิกการอัปโหลดแล้ว')
  }

  // Retry a failed R2 upload from the start (in-session). Per-part retry happens
  // inside startR2MultipartUpload; this re-runs the whole create on hard failure.
  const handleR2Retry = () => {
    if (!videoFile) return
    setR2Failed(false)
    void handleR2Create(form.getValues(), videoFile)
  }

  const onSubmit = async (data: FormData) => {
    submitting.onTrue()
    try {
      if (mode === 'create') {
        if (!videoFile) {
          toast.error('กรุณาเลือกไฟล์วิดีโอ')
          return
        }

        // R2 multipart path (flag ON). The legacy Firebase code below is left
        // untouched and only runs when the flag is OFF.
        if (CLIP_R2_UPLOAD_ENABLED) {
          await handleR2Create(data, videoFile)
          return
        }

        if (videoFile.size > 500 * 1024 * 1024) {
          toast.error('ไฟล์วิดีโอต้องมีขนาดไม่เกิน 500 MB')
          return
        }

        const tempId = uuidv4()
        const ext = videoFile.name.split('.').pop() ?? 'mp4'
        const videoPath = `admin-clips/raw/${tempId}.${ext}`
        const thumbPath = `admin-clips/raw-thumbnails/${tempId}.jpg`

        // Upload video
        setVideoProgress(0)
        const { storagePath: rawVideoPath } = await uploadToStorage(
          videoFile,
          videoPath,
          (pct) => setVideoProgress(pct),
        )
        setVideoProgress(100)

        // Upload thumbnail
        let rawThumbnailPath: string | undefined
        if (thumbnailDataUrl) {
          setThumbProgress(0)
          const thumbFile = dataUrlToFile(thumbnailDataUrl, `${tempId}.jpg`)
          const { storagePath } = await uploadToStorage(
            thumbFile,
            thumbPath,
            (pct) => setThumbProgress(pct),
          )
          rawThumbnailPath = storagePath
          setThumbProgress(100)
        }

        await axiosInstance.post(endpoints.clips.create, {
          title: data.title,
          description: data.description,
          rawVideoPath,
          rawThumbnailPath,
          durationSec,
          ...(videoMetadata ?? {}),
          tags: data.tags,
          category: data.category,
          isPublished: data.isPublished,
        })

        toast.success('อัปโหลดสำเร็จ — กำลังประมวลผลวิดีโอ')
        if (onSuccess) {
          onSuccess()
        } else {
          router.replace(paths.clips)
        }
      } else {
        // Edit mode: PATCH with changed fields only
        const changedFields: Record<string, unknown> = {}
        if (data.title !== clip?.title) changedFields.title = data.title
        if (data.description !== clip?.description)
          changedFields.description = data.description
        if (data.category !== clip?.category)
          changedFields.category = data.category
        if (data.isPublished !== clip?.isPublished)
          changedFields.isPublished = data.isPublished
        if (data.tags.join(',') !== (clip?.tags ?? []).join(','))
          changedFields.tags = data.tags

        await axiosInstance.patch(
          endpoints.clips.update(clip!.id),
          changedFields,
        )
        toast.success('บันทึกคลิปแล้ว')
        if (onSuccess) {
          onSuccess()
        } else {
          router.replace(paths.clips)
        }
      }
    } catch (err) {
      console.error('submit clip error', err)
      toast.error('อัปโหลดล้มเหลว')
    } finally {
      submitting.onFalse()
      setVideoProgress(null)
      setThumbProgress(null)
    }
  }

  const activeCategories = [...categories]
    .filter((c) => c.isEnabled)
    .sort((a, b) => a.order - b.order)
  const currentCategory = form.watch('category')
  const categoryOptions =
    currentCategory &&
    !activeCategories.some((category) => category.id === currentCategory)
      ? [
          {
            id: currentCategory,
            label: 'ทั้งหมด',
            order: 0,
            isEnabled: true,
            createdAt: '',
            updatedAt: '',
          },
          ...activeCategories,
        ]
      : activeCategories
  const isDialogVariant = variant === 'dialog'

  return (
    <div className="min-w-0">
      <Form {...form}>
        <form
          id="clip-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="min-w-0 space-y-4 sm:space-y-6"
        >
          <div className="min-w-0 space-y-4 sm:space-y-6">
            {/* Main info card */}
            <Card
              className={
                isDialogVariant
                  ? 'min-w-0 gap-4 rounded-none border-0 py-0 shadow-none'
                  : 'min-w-0 overflow-hidden rounded-lg border'
              }
            >
              <CardHeader className={isDialogVariant ? 'px-0' : 'px-4 sm:px-6'}>
                <CardTitle className="text-base sm:text-lg font-semibold text-black">
                  ข้อมูลคลิป
                </CardTitle>
              </CardHeader>
              <CardContent
                className={
                  isDialogVariant
                    ? 'space-y-4 px-0 pt-0 sm:space-y-5'
                    : 'px-4 sm:px-6 pt-0 space-y-4 sm:space-y-6'
                }
              >
                {/* Title */}
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        ชื่อคลิป *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="กรอกชื่อคลิป"
                          className="rounded-lg border h-10 sm:h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        รายละเอียด
                      </FormLabel>
                      <FormControl>
                        <div className="min-w-0">
                          <RichTextEditor
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="กรอกรายละเอียดคลิป"
                            className="min-h-[120px] sm:min-h-[160px]"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Category */}
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        หมวดหมู่
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="h-10 sm:h-11">
                            <SelectValue placeholder="เลือกหมวดหมู่" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categoryOptions.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tags */}
                <div className="space-y-3">
                  <Label className="text-sm sm:text-base font-medium text-black">
                    แท็ก
                  </Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="เพิ่มแท็กใหม่"
                      className="rounded-lg border h-10 sm:h-11 flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddTag()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTag}
                      disabled={!newTag.trim()}
                      variant="outline"
                      className="h-10 sm:h-11 w-full sm:w-auto"
                    >
                      <Plus className="h-4 w-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden">เพิ่มแท็ก</span>
                    </Button>
                  </div>
                  {form.watch('tags').length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.watch('tags').map((tag, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="gap-1 text-xs"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* isPublished toggle */}
                <div className="rounded-lg border p-3 sm:p-4">
                  <FormField
                    control={form.control}
                    name="isPublished"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="font-medium text-black text-sm">
                          เผยแพร่คลิป
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {mode === 'create' && (
              <Card
                className={
                  isDialogVariant
                    ? 'min-w-0 gap-4 rounded-none border-0 border-t py-0 pt-5 shadow-none'
                    : 'min-w-0 overflow-hidden rounded-lg border'
                }
              >
                <CardHeader className={isDialogVariant ? 'px-0' : 'px-4 sm:px-6'}>
                  <CardTitle className="text-base sm:text-lg font-semibold text-black">
                    ไฟล์วิดีโอ
                  </CardTitle>
                </CardHeader>
                <CardContent
                  className={
                    isDialogVariant
                      ? 'space-y-5 px-0 pt-0'
                      : 'space-y-6 px-4 pt-0 sm:px-6'
                  }
                >
                  <ClipVideoUploader
                    value={videoFile}
                    onChange={handleVideoChange}
                    onDurationChange={setDurationSec}
                    onMetadataChange={setVideoMetadata}
                    uploadProgress={videoProgress}
                    disabled={isUploading}
                    maxBytes={
                      CLIP_R2_UPLOAD_ENABLED
                        ? CLIP_MAX_UPLOAD_BYTES
                        : LEGACY_MAX_UPLOAD_BYTES
                    }
                  />

                  {/* R2 multipart upload: per-part progress + abort/retry (flag ON). */}
                  {CLIP_R2_UPLOAD_ENABLED && r2Progress && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          อัปโหลดวิดีโอ (R2) ·{' '}
                          {(r2Progress.uploadedBytes / 1048576).toFixed(1)}/
                          {(r2Progress.totalBytes / 1048576).toFixed(1)}MB
                        </p>
                        <p className="text-xs font-medium text-muted-foreground">
                          {Math.round(r2Progress.percent)}%
                        </p>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-100"
                          style={{ width: `${r2Progress.percent}%` }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCancelConfirmOpen(true)}
                          className="h-8 text-xs"
                        >
                          ยกเลิกการอัปโหลด
                        </Button>
                      </div>
                    </div>
                  )}

                  {CLIP_R2_UPLOAD_ENABLED && r2Failed && (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                      <span className="text-xs text-destructive">
                        อัปโหลดล้มเหลว กรุณาลองใหม่อีกครั้ง
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleR2Retry}
                        className="h-8 shrink-0 text-xs"
                      >
                        ลองใหม่
                      </Button>
                    </div>
                  )}

                  {thumbProgress !== null && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">
                        อัปโหลดภาพปก...
                      </p>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-100"
                          style={{ width: `${thumbProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {durationSec > 0 && (
                    <p className="text-xs text-muted-foreground">
                      ความยาว: {Math.floor(durationSec / 60)}:
                      {/* Floor the seconds: durationSec can be a raw float, so
                          without Math.floor this renders e.g. "4:9.0572…". */}
                      {String(Math.floor(durationSec % 60)).padStart(2, '0')} น.
                    </p>
                  )}

                  {videoFile && (
                    <ClipThumbnailPicker
                      value={thumbnailDataUrl}
                      onChange={setThumbnailDataUrl}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 py-4 sm:py-6 border-t">
            <Button
              type="submit"
              form="clip-form"
              disabled={submitting.value || isUploading}
              className="w-full sm:w-auto h-10 sm:h-11 order-2 sm:order-1"
            >
              {submitting.value
                ? mode === 'edit'
                  ? 'กำลังบันทึก...'
                  : 'กำลังอัปโหลด...'
                : mode === 'edit'
                  ? 'แก้ไข'
                  : 'สร้าง'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel ?? (() => router.push(paths.clips))}
              disabled={submitting.value || isUploading}
              className="w-full sm:w-auto h-10 sm:h-11 order-1 sm:order-2"
            >
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>

      {mode === 'edit' && variant === 'page' && clip?.id && (
        <div className="mt-4 sm:mt-6">
          <ClipCommentsManager clipId={clip.id} />
        </div>
      )}

      {/* Cancel-upload confirmation: the only exit while an upload is locked. */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title="ยกเลิกการอัพโหลด"
        description="ไฟล์ที่อัพโหลดไปแล้วจะถูกลบและต้องเริ่มใหม่ทั้งหมด ต้องการยกเลิกหรือไม่?"
        confirmText="ยกเลิกการอัพโหลด"
        cancelText="อัพโหลดต่อ"
        variant="destructive"
        onConfirm={handleR2Abort}
      />
    </div>
  )
}
